import { useState, useEffect, useCallback } from 'react'
import API from 'common/logic/API'
import Utils from 'common/Utils'

interface PaginatedFetchResult<T> {
    results: T[] | null
    error: unknown
    hasMore: boolean
    start: number
    sort: string | null
    fetchValues: (newStart?: number, newSort?: string | null) => void
}

export function usePaginatedFetch<T = unknown>(
    endpoint: string,
    endpointParams?: Record<string, unknown>,
    refreshKey?: unknown
): PaginatedFetchResult<T> {
    const [results, setResults] = useState<T[] | null>(null)
    const [error, setError] = useState<unknown>(null)
    const [hasMore, setHasMore] = useState(false)
    const [start, setStart] = useState(0)
    const [sort, setSort] = useState<string | null>(null)

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const fetch = useCallback(
        (newStart?: number, newSort?: string | null) => {
            setResults(null)
            setError(null)

            const params: Record<string, unknown> = {}
            const toDelete: string[] = []
            if (newStart && newStart > 0) {
                params.start = newStart
            } else {
                toDelete.push('start')
            }
            if (newSort != null) {
                params.sort = newSort
            }

            const newUrl = Utils.currentUrlWithParams(params, toDelete)
            if (newUrl !== window.location.href) {
                window.history.pushState(null, '', newUrl)
            }

            API.get(endpoint, { ...params, ...(endpointParams || {}) })
                .then(({ data }) => {
                    const d = data as any
                    if (d && d.results !== undefined) {
                        setResults(d.results as T[])
                        setHasMore(!!d.hasMore)
                        setStart(d.start != null ? d.start : newStart || 0)
                    } else {
                        setError('Unexpected response format')
                    }
                })
                .catch((err: unknown) => setError(err))
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [endpoint, JSON.stringify(endpointParams || {})]
    )

    useEffect(() => {
        const q = new URLSearchParams(window.location.search)
        const urlStart = parseInt(q.get('start') || '0', 10)
        const urlSort = q.get('sort') || null
        setStart(urlStart)
        setSort(urlSort)
        fetch(urlStart, urlSort)
        // refreshKey is intentionally included so callers can force a re-fetch
    }, [fetch, refreshKey]) // eslint-disable-line react-hooks/exhaustive-deps

    const fetchValues = useCallback(
        (newStart?: number, newSort?: string | null) => {
            const s = newStart != null ? newStart : 0
            const so = newSort !== undefined ? newSort : null
            setStart(s)
            setSort(so)
            fetch(s, so)
        },
        [fetch]
    )

    return { results, error, hasMore, start, sort, fetchValues }
}
